import CheckOutlined from "@mui/icons-material/CheckOutlined";
import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import pluralize from "pluralize";
import { useMemo, useState } from "react";

export type FacilityRowData = {
  reportId: string | null | undefined;
  organizationName: string;
  facilityName: string;
  facilityAddress: string;
  facilityId: string;
  tenantId?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (note: string) => Promise<void>;
  loading: boolean;
  facilities: FacilityRowData[];
};

export function AddFulfillmentNoteDialog({ open, onClose, onSubmit, loading, facilities }: Props) {
  const [noteText, setNoteText] = useState("");

  const handleClose = () => {
    setNoteText("");
    onClose();
  };

  const handleSave = async () => {
    await onSubmit(noteText);
    setNoteText("");
  };

  const { facilityCount, tenantCount } = useMemo(() => {
    const uniqueTenants = new Set(facilities.map((f) => f.tenantId ?? f.organizationName));
    return {
      facilityCount: facilities.length,
      tenantCount: uniqueTenants.size,
    };
  }, [facilities]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      title="Add Fulfillment Note"
      primaryActionButtonProps={{
        onClick: handleSave,
        loading,
        saveText: "Save",
        startIcon: <CheckOutlined />,
      }}
    >
      <Typography variant="body1" color="text.secondary">
        Record bespoke reporting needs here. This note will override any pre-existing notes on the
        selected facilities and reports.
      </Typography>

      <TextField
        label="Add note"
        multiline
        fullWidth
        minRows={2}
        maxRows={6}
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        placeholder="Add note"
      />

      <Divider />

      <Typography variant="caption" color="text.secondary">
        Selection includes: {facilityCount} {pluralize("facility", facilityCount)} across{" "}
        {tenantCount} {pluralize("tenant", tenantCount)}
      </Typography>

      <TableContainer sx={{ maxHeight: 300 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Organization Name</TableCell>
              <TableCell>Facility</TableCell>
              <TableCell>Facility ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {facilities.map((facility, index) => (
              <TableRow key={facility.reportId ?? index}>
                <TableCell>{facility.organizationName}</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{facility.facilityName}</Typography>
                    <Typography variant="caption" sx={{ fontSize: "0.625rem" }}>
                      {facility.facilityAddress}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{facility.facilityId}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Dialog>
  );
}
